'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  ArrowDown, ArrowUp, Download, ExternalLink, Eye, EyeOff, Flame, Globe, LayoutGrid,
  LogIn, Mail, Pencil, Phone, Plus, Search, Settings2, Trash2, Users,
} from 'lucide-react';
import { AdminModal } from '@/components/admin/AdminModal';
import { useToast } from '@/components/admin/Toast';
import { CreateTenantModal } from '../CreateTenantModal';
import { FEATURE_LABELS, parseTenantFeatures, type TenantFeatures, type TenantRow, type TenantUser } from '../types';

type Props = {
  tenants: TenantRow[];
  loading: boolean;
  onRefresh: () => void;
};

export function TenantsPanel({ tenants, loading, onRefresh }: Props) {
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<TenantRow | null>(null);
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newDomain, setNewDomain] = useState({ host: '', is_primary: false });
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'staff' });
  const [editingUser, setEditingUser] = useState<TenantUser | null>(null);
  const [editUser, setEditUser] = useState({ name: '', email: '', password: '', role: 'staff', is_active: true });
  const [showCreate, setShowCreate] = useState(false);
  const [features, setFeatures] = useState<TenantFeatures | null>(null);
  const [communicationsAccountId, setCommunicationsAccountId] = useState('');

  useEffect(() => {
    if (selected) {
      setFeatures(parseTenantFeatures(selected.settings));
      setCommunicationsAccountId(selected.communications_account_id ?? '');
    }
  }, [selected]);

  useEffect(() => {
    if (!selected) return;
    const fresh = tenants.find((tenant) => tenant.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [tenants, selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q) ||
        t.domains.some((d) => d.host.toLowerCase().includes(q)),
    );
  }, [tenants, query]);

  const primaryHost = (t: TenantRow) =>
    t.domains.find((d) => d.is_primary && d.is_active)?.host ??
    t.domains.find((d) => d.is_active)?.host ??
    `${t.slug}.okai.pt`;

  const toggleLandingVisible = async (tenant: TenantRow) => {
    const res = await fetch(`/api/super-admin/tenants/${tenant.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ landing_visible: !tenant.landing_visible }),
    });
    if (res.ok) {
      toast(tenant.landing_visible ? 'Oculto da landing' : 'Visivel na landing');
      onRefresh();
    } else {
      toast('Erro ao atualizar landing', 'error');
    }
  };

  const moveTenant = async (index: number, direction: -1 | 1) => {
    const list = [...filtered];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= list.length) return;
    [list[index], list[newIndex]] = [list[newIndex], list[index]];
    const orders = list.map((t, i) => ({ id: t.id, landing_order: i + 1 }));
    await fetch('/api/super-admin/tenants/reorder', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orders }),
    });
    onRefresh();
  };

  const addDomain = async () => {
    if (!selected) return;
    const res = await fetch('/api/super-admin/domains', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ restaurant_id: selected.id, ...newDomain }),
    });
    if (res.ok) {
      toast('Dominio adicionado');
      setShowAddDomain(false);
      setNewDomain({ host: '', is_primary: false });
      onRefresh();
    } else {
      toast('Erro ao adicionar dominio', 'error');
    }
  };

  const deleteDomain = async (domainId: string) => {
    await fetch(`/api/super-admin/domains/${domainId}`, { method: 'DELETE' });
    toast('Dominio removido');
    onRefresh();
  };

  const addUser = async () => {
    if (!selected) return;
    const res = await fetch('/api/super-admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ restaurant_id: selected.id, ...newUser }),
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      toast(data.email_sent === false ? data.warning : 'Utilizador criado e convite enviado', data.email_sent === false ? 'error' : 'success');
      setShowAddUser(false);
      setNewUser({ name: '', email: '', role: 'staff' });
      onRefresh();
    } else {
      const data = await res.json().catch(() => ({}));
      toast(data.error || 'Erro ao criar utilizador', 'error');
    }
  };

  const resendInvite = async (userId: string) => {
    const res = await fetch(`/api/super-admin/users/${userId}/invite`, { method: 'POST' });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      toast('Convite reenviado');
      onRefresh();
    } else {
      toast(data.error || 'Erro ao reenviar convite', 'error');
    }
  };

  const deleteUser = async (userId: string) => {
    if (!window.confirm('Eliminar definitivamente este utilizador?')) return;
    const res = await fetch(`/api/super-admin/users/${userId}`, { method: 'DELETE' });
    if (res.ok) {
      toast('Utilizador eliminado');
      onRefresh();
    } else {
      const data = await res.json().catch(() => ({}));
      toast(data.error || 'Erro ao eliminar utilizador', 'error');
    }
  };

  const startEditUser = (user: TenantUser) => {
    setEditingUser(user);
    setEditUser({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      is_active: user.is_active,
    });
  };

  const saveUser = async () => {
    if (!editingUser) return;
    const payload = {
      name: editUser.name,
      email: editUser.email,
      role: editUser.role,
      is_active: editUser.is_active,
      ...(editUser.password ? { password: editUser.password } : {}),
    };
    const res = await fetch(`/api/super-admin/users/${editingUser.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      toast('Utilizador atualizado');
      setEditingUser(null);
      onRefresh();
    } else {
      const data = await res.json().catch(() => ({}));
      toast(data.error || 'Erro ao atualizar utilizador', 'error');
    }
  };

  const impersonate = async (tenantId: string) => {
    const res = await fetch(`/api/super-admin/tenants/${tenantId}/impersonate`, { method: 'POST' });
    const data = await res.json();
    if (res.ok && data.redirect_url) {
      toast(`A entrar como ${data.session?.email ?? 'admin'}...`);
      window.location.href = data.redirect_url;
    } else {
      toast(data.error || 'Erro ao impersonar', 'error');
    }
  };

  const saveFeatures = async () => {
    if (!selected || !features) return;
    const settings = {
      ...(selected.settings ?? {}),
      features,
      public_ordering: features.public_ordering,
      voice_room_assistant: features.voice_room_assistant,
      voice_agent: { enabled: features.voice_agent },
    };
    const res = await fetch(`/api/super-admin/tenants/${selected.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ settings }),
    });
    if (res.ok) {
      toast('Funcionalidades atualizadas');
      onRefresh();
    } else {
      toast('Erro ao guardar features', 'error');
    }
  };

  const saveCommunicationsAccount = async () => {
    if (!selected || selected.communications_account_id) return;
    const accountId = communicationsAccountId.trim();
    if (!accountId) {
      toast('Indique o account UUID provisionado no telephony-core', 'error');
      return;
    }
    const res = await fetch(`/api/super-admin/tenants/${selected.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ communications_account_id: accountId }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      toast('Identidade de comunicacoes provisionada');
      onRefresh();
    } else {
      toast(data.error || 'Erro ao provisionar identidade', 'error');
    }
  };

  if (loading) {
    return <p className="text-sm text-ink-500">A carregar tenants...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="sa-toolbar">
        <div className="sa-search">
          <Search className="w-4 h-4 text-ink-400" />
          <input
            type="search"
            placeholder="Pesquisar por nome, slug ou dominio..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="sa-search-input"
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-500">
          <LayoutGrid className="w-4 h-4" />
          <span>{filtered.length} restaurantes</span>
        </div>
        <div className="flex gap-2">
          <a href="/api/super-admin/export?type=tenants" className="btn btn-secondary btn-sm">
            <Download className="w-4 h-4" /> CSV
          </a>
          <button type="button" onClick={() => setShowCreate(true)} className="btn btn-sm bg-brand text-white">
            <Plus className="w-4 h-4" /> Novo tenant
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((t, index) => (
          <article
            key={t.id}
            className="sa-tenant-card"
          >
            <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => moveTenant(index, -1)}
                    disabled={index === 0}
                    className="sa-order-btn"
                    title="Mover para cima"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-[10px] text-center font-mono text-ink-400">{t.landing_order}</span>
                  <button
                    type="button"
                    onClick={() => moveTenant(index, 1)}
                    disabled={index === filtered.length - 1}
                    className="sa-order-btn"
                    title="Mover para baixo"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center flex-shrink-0">
                  <Flame className="w-5 h-5 text-brand" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-ink-900">{t.name}</h3>
                    <span className={`sa-status sa-status-${t.status === 'active' ? 'ok' : 'muted'}`}>{t.status}</span>
                  </div>
                  <p className="text-xs text-ink-500 font-mono mt-0.5">{t.slug}</p>
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-ink-500">
                    <span>{t.domains.length} dominios</span>
                    <span>{t.users.filter((u) => u.is_active).length} users</span>
                    <span>{t.voice_numbers.filter((v) => v.is_active).length} DIDs</span>
                    <span>{t.orders_7d} pedidos / 7d</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                <button
                  type="button"
                  onClick={() => toggleLandingVisible(t)}
                  className={`sa-chip ${t.landing_visible ? 'sa-chip-brand' : ''}`}
                >
                  {t.landing_visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {t.landing_visible ? 'Landing' : 'Oculto'}
                </button>
                <a
                  href={`https://${primaryHost(t)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="sa-chip"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Site
                </a>
                <a
                  href={`https://${primaryHost(t)}/admin`}
                  target="_blank"
                  rel="noreferrer"
                  className="sa-chip"
                >
                  <Settings2 className="w-3.5 h-3.5" /> Admin
                </a>
                <button
                  type="button"
                  onClick={() => impersonate(t.id)}
                  className="sa-chip"
                  title="Entrar como admin deste tenant"
                >
                  <LogIn className="w-3.5 h-3.5" /> Entrar como
                </button>
                <button
                  type="button"
                  onClick={() => setSelected(t)}
                  className="btn btn-sm bg-ink-900 text-white"
                >
                  Gerir
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      <AdminModal
        open={!!selected}
        onClose={() => {
          setSelected(null);
          setShowAddDomain(false);
          setShowAddUser(false);
          setEditingUser(null);
        }}
        title={selected?.name ?? 'Tenant'}
        description={selected ? `Slug: ${selected.slug}` : undefined}
        size="xl"
      >
        {selected && (
          <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Dominios
                </h4>
                <button type="button" onClick={() => setShowAddDomain(!showAddDomain)} className="btn btn-secondary btn-sm">
                  <Plus className="w-3 h-3" /> Adicionar
                </button>
              </div>
              <div className="space-y-1">
                {selected.domains.map((d) => (
                  <div key={d.id} className="sa-list-row">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-sm truncate">{d.host}</span>
                      {d.is_primary && <span className="sa-tag">primario</span>}
                      {!d.is_active && <span className="sa-tag sa-tag-muted">inativo</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`https://${d.host}`} target="_blank" rel="noreferrer" className="text-ink-400 hover:text-brand">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      <button type="button" onClick={() => deleteDomain(d.id)} className="text-ink-400 hover:text-danger">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {showAddDomain && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    placeholder="novo.okai.pt"
                    value={newDomain.host}
                    onChange={(e) => setNewDomain({ ...newDomain, host: e.target.value })}
                    className="input-field text-sm flex-1 min-w-[200px]"
                  />
                  <label className="flex items-center gap-1 text-xs text-ink-500">
                    <input
                      type="checkbox"
                      checked={newDomain.is_primary}
                      onChange={(e) => setNewDomain({ ...newDomain, is_primary: e.target.checked })}
                    />
                    primario
                  </label>
                  <button type="button" onClick={addDomain} className="btn btn-sm bg-brand text-white">OK</button>
                </div>
              )}
            </section>

            {features && (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold">Funcionalidades</h4>
                  <button type="button" onClick={saveFeatures} className="btn btn-secondary btn-sm">Guardar</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(Object.keys(FEATURE_LABELS) as Array<keyof TenantFeatures>).map((key) => (
                    <label key={key} className="flex items-center gap-2 text-sm text-ink-700 sa-list-row">
                      <input
                        type="checkbox"
                        checked={features[key]}
                        onChange={(e) => setFeatures({ ...features, [key]: e.target.checked })}
                      />
                      {FEATURE_LABELS[key]}
                    </label>
                  ))}
                </div>
                {features.demo_quick_access ? (
                  <p className="mt-2 text-xs text-ink-500">
                    Mostra botoes de acesso rapido (admin/funcionario) em /auth, PWA takeaway, mesa QR, waiter e admin — incluindo em producao. Nao afeta o super-admin.
                  </p>
                ) : null}
                {features.crm ? (
                  <p className="mt-2 text-xs text-ink-500">
                    Ativa CRM no admin, login de cliente nas PWAs, pontos/fidelizacao, campanhas e personalizacao da voz pelo telefone do cliente.
                  </p>
                ) : null}
              </section>
            )}

            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">Identidade de comunicacoes</h4>
                <span className={`sa-status sa-status-${selected.communications_account_id ? 'ok' : 'muted'}`}>
                  {selected.communications_account_id ? 'Provisionada' : 'Pendente'}
                </span>
              </div>
              <p className="mb-2 text-xs text-ink-500">
                UUID do account criado no telephony-core. Nao e o ID local nem o slug do restaurante.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  className="input-field flex-1 font-mono text-sm"
                  placeholder="00000000-0000-0000-0000-000000000000"
                  value={communicationsAccountId}
                  disabled={Boolean(selected.communications_account_id)}
                  onChange={(e) => setCommunicationsAccountId(e.target.value.trim())}
                />
                {!selected.communications_account_id && (
                  <button type="button" onClick={saveCommunicationsAccount} className="btn btn-secondary btn-sm">
                    Provisionar
                  </button>
                )}
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Numeros de voz
                </h4>
              </div>
              {selected.voice_numbers.length === 0 ? (
                <p className="text-xs text-ink-500">Nenhum DID configurado.</p>
              ) : (
                <div className="space-y-1">
                  {selected.voice_numbers.map((v) => (
                    <div key={v.id} className="sa-list-row items-start">
                      <div className="min-w-0">
                        <span className="font-mono text-sm">+{v.did_number}</span>
                        <code className="mt-1 block break-all text-[10px] text-ink-400">
                          churrasqueira:voice-number:{v.id}
                        </code>
                      </div>
                      <span className="text-xs text-ink-500">{v.provider}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" /> Utilizadores
                </h4>
                <button type="button" onClick={() => setShowAddUser(!showAddUser)} className="btn btn-secondary btn-sm">
                  <Plus className="w-3 h-3" /> Adicionar
                </button>
              </div>
              <div className="space-y-1">
                {selected.users.map((u) => (
                  <div key={u.id} className="sa-list-row">
                    <div className="min-w-0">
                      <span className="text-sm font-medium">{u.name}</span>
                      <span className="text-xs text-ink-500 ml-2">{u.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="sa-tag">{u.role}</span>
                      <span className={`sa-status sa-status-${u.is_active ? 'ok' : 'muted'}`}>
                        {u.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                      {u.password_setup_required && <span className="sa-tag sa-tag-muted">Convite pendente</span>}
                      {u.password_setup_required && (
                        <button type="button" onClick={() => resendInvite(u.id)} className="text-ink-400 hover:text-brand" title="Reenviar convite">
                          <Mail className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button type="button" onClick={() => startEditUser(u)} className="text-ink-400 hover:text-brand" title="Editar">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => deleteUser(u.id)} className="text-ink-400 hover:text-danger">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {showAddUser && (
                <div className="mt-2 space-y-2 p-3 bg-surface-2 rounded-lg">
                  <input type="text" placeholder="Nome" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="input-field text-sm" />
                  <input type="email" placeholder="email@exemplo.pt" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="input-field text-sm" />
                  <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="input-field text-sm">
                    <option value="staff">Funcionario</option>
                    <option value="admin">Admin</option>
                  </select>
                  <p className="text-xs text-ink-500">A pessoa recebe um link individual para definir a palavra-passe.</p>
                  <button type="button" onClick={addUser} className="btn btn-sm bg-brand text-white">Adicionar</button>
                </div>
              )}
              {editingUser && (
                <div className="mt-2 space-y-2 p-3 bg-surface-2 rounded-lg">
                  <p className="text-sm font-semibold">Editar utilizador</p>
                  <input type="text" placeholder="Nome" value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} className="input-field text-sm" />
                  <input type="email" placeholder="email@exemplo.pt" value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} className="input-field text-sm" />
                  <input type="password" placeholder="Nova password (deixar vazio para manter)" value={editUser.password} onChange={(e) => setEditUser({ ...editUser, password: e.target.value })} className="input-field text-sm" />
                  <div className="flex flex-wrap gap-2">
                    <select value={editUser.role} onChange={(e) => setEditUser({ ...editUser, role: e.target.value })} className="input-field text-sm flex-1">
                      <option value="staff">Funcionario</option>
                      <option value="admin">Admin</option>
                    </select>
                    <label className="flex items-center gap-2 text-sm px-2">
                      <input type="checkbox" checked={editUser.is_active} onChange={(e) => setEditUser({ ...editUser, is_active: e.target.checked })} />
                      Ativo
                    </label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setEditingUser(null)} className="btn btn-secondary btn-sm">Cancelar</button>
                    <button type="button" onClick={saveUser} className="btn btn-sm bg-brand text-white">Guardar</button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </AdminModal>
      <CreateTenantModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={onRefresh} />
    </div>
  );
}
